import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../../components/apiconfig/api-config';

const roles = ['account', 'purchase', 'sale', 'finance', 'admin', 'manager', 'auditor', 'backup'];

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const navigate = useNavigate();

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/api/auth/user-list`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleEditClick = (user) => {
    setEditUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      is_active: user.is_active ? 1 : 0,
    });
    setEditError('');
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        is_active: editForm.is_active,
      };
      if (editForm.password) payload.password = editForm.password;
      await axios.put(`${API_BASE_URL}/api/auth/user-list/${editUser.id}`, payload, { withCredentials: true });
      setEditUser(null);
      setEditForm({});
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditUser(null);
    setEditForm({});
    setEditError('');
  };

  return (
    <div className="max-w-8xl mx-auto p-2 bg-white rounded border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
        <h2 className="text-xl font-semibold">User Records</h2>
        <button
          className="flex items-center gap-1 bg-[#2691cc] text-white px-3 py-1 text-xs rounded hover:bg-[#0e8dc6] border border-[#10a5e9] font-medium"
          onClick={() => navigate('/register')}
        >
          Add New User
        </button>
      </div>
      {loading ? (
        <div className="text-center text-[#2691cc] py-6 text-sm">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-600 py-6 text-sm">{error}</div>
      ) : users.length === 0 ? (
        <div className="text-center text-gray-500 py-6 text-sm">No user records found.</div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-50">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Name</th>
                <th className="px-3 py-2 text-left font-medium">Email</th>
                <th className="px-3 py-2 text-left font-medium">Role</th>
                <th className="px-3 py-2 text-left font-medium">Active</th>
                <th className="px-3 py-2 text-left font-medium">Last Login</th>
                <th className="px-3 py-2 text-left font-medium">Created At</th>
                <th className="px-3 py-2 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user, idx) => (
                <tr key={user.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-1 font-semibold">{user.name}</td>
                  <td className="px-3 py-1">{user.email}</td>
                  <td className="px-3 py-1">{user.role}</td>
                  <td className="px-3 py-1">{user.is_active ? 'Active' : 'Inactive'}</td>
             <td className="px-3 py-1">
  {user.last_login
    ? new Date(user.last_login).toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // for 24-hour format; remove this line for 12-hour format
      })
    : '-'}
</td>
<td className="px-3 py-1">
  {new Date(user.created_at).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })}
</td>

                  <td className="px-3 py-1">
                    <button
                      className="bg-[#2691cc] text-white px-3 py-1 rounded text-xs hover:bg-[#2691cc] transition-colors"
                      onClick={() => handleEditClick(user)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <h3 className="text-lg font-semibold mb-4">Edit User</h3>
            {editError && <div className="mb-2 p-2 bg-red-100 text-red-700 rounded">{editError}</div>}
            <form className="space-y-4" onSubmit={handleEditSubmit}>
              <input
                type="text"
                name="name"
                value={editForm.name}
                onChange={handleEditChange}
                placeholder="Full Name"
                required
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="email"
                name="email"
                value={editForm.email}
                onChange={handleEditChange}
                placeholder="Email"
                required
                className="w-full px-3 py-2 border rounded"
              />
              <input
                type="password"
                name="password"
                value={editForm.password}
                onChange={handleEditChange}
                placeholder="New Password (leave blank to keep unchanged)"
                className="w-full px-3 py-2 border rounded"
              />
              <select
                name="role"
                value={editForm.role}
                onChange={handleEditChange}
                required
                className="w-full px-3 py-2 border rounded"
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                ))}
              </select>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={!!editForm.is_active}
                  onChange={handleEditChange}
                  className="mr-2"
                />
                <label htmlFor="is_active" className="text-sm">Active</label>
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  type="submit"
                  className="bg-[#2691cc] text-white px-4 py-2 rounded hover:bg-blue-700"
                  disabled={editLoading}
                >
                  {editLoading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  onClick={handleEditCancel}
                  disabled={editLoading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
